import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiStar,
  FiEye,
  FiEyeOff,
  FiSearch,
  FiUsers,
  FiTrendingUp,
  FiGrid,
  FiList
} from 'react-icons/fi'
import { useArtists } from '../../hooks/useArtists'
import { Artist } from '../../types/artist'
import DraggableArtistList from './DraggableArtistList'

const ArtistManagement: React.FC = () => {
  const navigate = useNavigate()
  const { artists, loading, toggleFeatured, toggleActive, deleteArtist, reorderArtists } = useArtists()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterFeatured, setFilterFeatured] = useState<boolean | null>(null)
  const [filterActive, setFilterActive] = useState<boolean | null>(null)
  const [selectedArtists, setSelectedArtists] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'grid' | 'reorder'>('grid')

  const filteredArtists = artists.filter(artist => {
    const matchesSearch = artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         artist.genre.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesFeatured = filterFeatured === null || artist.featured === filterFeatured
    const matchesActive = filterActive === null || artist.active === filterActive
    
    return matchesSearch && matchesFeatured && matchesActive
  })

  const stats = {
    total: artists.length,
    featured: artists.filter(a => a.featured).length,
    active: artists.filter(a => a.active).length,
    inactive: artists.filter(a => !a.active).length
  }

  const handleToggleFeatured = async (artistId: string, featured: boolean) => {
    await toggleFeatured(artistId, !featured)
  }

  const handleToggleActive = async (artistId: string, active: boolean) => {
    await toggleActive(artistId, !active)
  }

  const handleDeleteArtist = async (artistId: string, artistName: string) => {
    if (window.confirm(`Are you sure you want to delete "${artistName}"? This action cannot be undone.`)) {
      await deleteArtist(artistId)
    }
  }

  const handleReorderArtists = async (reorderedArtists: Artist[]) => {
    await reorderArtists(reorderedArtists)
  }

  const handleEditArtist = (artistId: string) => {
    navigate(`/admin/artists/edit/${artistId}`)
  }

  const handleSelectArtist = (artistId: string) => {
    setSelectedArtists(prev => 
      prev.includes(artistId) 
        ? prev.filter(id => id !== artistId)
        : [...prev, artistId]
    )
  }

  const handleSelectAll = () => {
    if (selectedArtists.length === filteredArtists.length) {
      setSelectedArtists([])
    } else {
      setSelectedArtists(filteredArtists.map(artist => artist.id))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading artists...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {/* Background Gradient Flares */}
      <div className="absolute top-10 right-10 w-[600px] h-[600px] bg-gradient-to-bl from-blue-200/80 to-purple-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-32 left-10 w-[500px] h-[500px] bg-gradient-to-tr from-purple-200/65 to-blue-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-1">Artist Management</h1>
          <p className="text-neutral-600 text-sm">
            Manage your roster of musicians and performers
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-neutral-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-md transition-all text-sm flex-1 sm:flex-initial ${
                viewMode === 'grid'
                  ? 'bg-white text-neutral-900 shadow-sm font-medium'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <FiGrid size={16} />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode('reorder')}
              className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-md transition-all text-sm flex-1 sm:flex-initial ${
                viewMode === 'reorder'
                  ? 'bg-white text-neutral-900 shadow-sm font-medium'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <FiList size={16} />
              <span>Reorder</span>
            </button>
          </div>

          <Link
            to="/admin/artists/add"
            className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-4 py-2.5 rounded-lg hover:shadow-md transition-all text-sm w-full sm:w-auto"
          >
            <FiPlus size={18} />
            <span>Add Artist</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Artists', value: stats.total, icon: FiUsers, gradient: 'from-blue-500/50 to-purple-500/50' },
          { label: 'Featured', value: stats.featured, icon: FiStar, gradient: 'from-purple-500/50 to-indigo-500/50' },
          { label: 'Active', value: stats.active, icon: FiEye, gradient: 'from-indigo-500/50 to-blue-500/50' },
          { label: 'Inactive', value: stats.inactive, icon: FiEyeOff, gradient: 'from-purple-500/50 to-blue-500/50' },
        ].map((stat, index) => (
          <div
            key={index}
            className={`p-[1px] bg-gradient-to-br ${stat.gradient} rounded-lg hover:shadow-lg transition-all`}
          >
            <div className="bg-white rounded-lg p-5 hover:bg-gradient-to-br hover:from-white hover:to-neutral-50 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-neutral-50 rounded-md">
                <stat.icon size={18} className="text-neutral-700" />
              </div>
              <FiTrendingUp size={14} className="text-emerald-500" />
            </div>
              <div>
                <p className="text-2xl font-semibold text-neutral-900 mb-1">{stat.value}</p>
                <p className="text-sm text-neutral-600">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="p-[1px] bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-indigo-500/50 rounded-lg">
        <div className="bg-white rounded-lg p-5">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
              <input
                type="text"
                placeholder="Search artists by name or genre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterFeatured === null ? '' : filterFeatured.toString()}
              onChange={(e) => setFilterFeatured(e.target.value === '' ? null : e.target.value === 'true')}
              className="w-full sm:w-auto px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
            >
              <option value="">All Featured Status</option>
              <option value="true">Featured Only</option>
              <option value="false">Not Featured</option>
            </select>

            <select
              value={filterActive === null ? '' : filterActive.toString()}
              onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
              className="w-full sm:w-auto px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
            >
              <option value="">All Status</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedArtists.length > 0 && (
          <div className="mt-4 p-4 bg-neutral-50 border border-neutral-200 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <span className="text-sm text-neutral-600">
              {selectedArtists.length} artist{selectedArtists.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex flex-col sm:flex-row gap-2">
              <button className="text-sm px-3 py-2 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 transition-colors">
                Mark Featured
              </button>
              <button className="text-sm px-3 py-2 bg-neutral-200 text-neutral-700 rounded-md hover:bg-neutral-300 transition-colors">
                Mark Active
              </button>
              <button className="text-sm px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors">
                Delete Selected
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Artists Section */}
      <div className="p-[1px] bg-gradient-to-br from-purple-500/50 to-blue-500/50 rounded-lg">
        <div className="bg-white rounded-lg">
        <div className="p-5 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-900">
              Artists ({filteredArtists.length})
              {viewMode === 'reorder' && (
                <span className="ml-2 text-sm font-normal text-neutral-600">
                  - Drag & Drop Mode
                </span>
              )}
            </h2>
            {viewMode === 'grid' && filteredArtists.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-purple-600 transition-colors"
              >
                {selectedArtists.length === filteredArtists.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
        </div>

        {filteredArtists.length === 0 ? (
          <div className="p-12 text-center">
            <FiUsers size={48} className="text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">
              {searchQuery || filterFeatured !== null || filterActive !== null ? 'No artists found' : 'No artists yet'}
            </h3>
            <p className="text-neutral-600 mb-6 text-sm">
              {searchQuery || filterFeatured !== null || filterActive !== null 
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first artist to the roster'
              }
            </p>
            {!searchQuery && filterFeatured === null && filterActive === null && (
              <Link
                to="/admin/artists/add"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-5 py-2.5 rounded-lg hover:shadow-md transition-all text-sm"
              >
                <FiPlus size={18} />
                <span>Add Your First Artist</span>
              </Link>
            )}
          </div>
        ) : (
          <div className="p-5">
            {viewMode === 'reorder' ? (
              <DraggableArtistList
                artists={filteredArtists.sort((a, b) => a.order - b.order)}
                onReorder={handleReorderArtists}
                onToggleFeatured={handleToggleFeatured}
                onToggleActive={handleToggleActive}
                onEdit={handleEditArtist}
                onDelete={handleDeleteArtist}
              />
            ) : (
              <div className="divide-y divide-neutral-200">
                {filteredArtists.map((artist) => (
                  <div
                    key={artist.id}
                    className="py-5 hover:bg-neutral-50 transition-colors -mx-5 px-5"
                  >
                    <div className="flex items-center space-x-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedArtists.includes(artist.id)}
                        onChange={() => handleSelectArtist(artist.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />

                      {/* Artist Image */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={artist.image}
                          alt={artist.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Artist Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="font-medium text-neutral-900 truncate">
                            {artist.name}
                          </h3>
                          {artist.featured && (
                            <FiStar className="text-yellow-500 fill-current" size={16} />
                          )}
                          {!artist.active && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-blue-600 mb-1">{artist.genre}</p>
                        <p className="text-sm text-neutral-600 line-clamp-2">
                          {artist.description}
                        </p>
                      </div>


                      {/* Actions */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleToggleFeatured(artist.id, artist.featured)}
                          className={`p-2 rounded-md transition-colors ${
                            artist.featured
                              ? 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200'
                              : 'text-neutral-400 hover:text-yellow-600 hover:bg-yellow-50'
                          }`}
                          title={artist.featured ? 'Remove from featured' : 'Mark as featured'}
                        >
                          <FiStar size={16} className={artist.featured ? 'fill-current' : ''} />
                        </button>

                        <button
                          onClick={() => handleToggleActive(artist.id, artist.active)}
                          className={`p-2 rounded-md transition-colors ${
                            artist.active
                              ? 'text-green-600 bg-green-100 hover:bg-green-200'
                              : 'text-red-600 bg-red-100 hover:bg-red-200'
                          }`}
                          title={artist.active ? 'Deactivate artist' : 'Activate artist'}
                        >
                          {artist.active ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                        </button>

                        <Link
                          to={`/admin/artists/edit/${artist.id}`}
                          className="p-2 rounded-md text-neutral-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit artist"
                        >
                          <FiEdit3 size={16} />
                        </Link>

                        <button
                          onClick={() => handleDeleteArtist(artist.id, artist.name)}
                          className="p-2 rounded-md text-neutral-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete artist"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default ArtistManagement